import { useMemo } from 'react'
import { differenceInYears, format, add, sub } from 'date-fns'
import { CarHireSearchFormParams } from '@reward-platform/ancillaries-schemas/car-hire'
import { CarHireLocation, isSameCarHirePlace } from '@reward-platform/ancillaries-locations'
import { useAncillariesLocationsSearch } from '~/hooks/useAncillariesLocations'
import { useFeatureFlag } from '~/context/featureFlag'
import { CarHireSearchForm } from '../SearchCarHireForm/SearchCarHireForm.types'

const DEFAULT_LOCATION: CarHireLocation = {
  id: '',
  type: '',
  name: '',
  fullName: '',
  countryCode: 'GB',
  afterHoursIndicator: true,
  telephoneNumber: '',
  location: [{ latitude: '', longitude: '' }],
  openingTimes: [],
  address: [''],
  vendorBrandId: '1',
}

const DRIVER_AGE_LIMIT_IN_YEARS = 25
const DRIVER_AGE_LIMIT_BEFORE_DAYS = 2

const getTimes = (date: string) => {
  return date ? format(new Date(date), 'HH:mm') : '10:00'
}

const useLocationsSearchResolver = (
  search: {
    id: string
    type: string
    countryCode?: string
    vendorBrandId?: string
    multiBrand: boolean
  },
  options: { isEnabled: boolean }
) => {
  const { data, isLoading } = useAncillariesLocationsSearch(
    'CAR_HIRE',
    {
      searchString: search.id,
      vendorBrandId: search.multiBrand ? undefined : search.vendorBrandId,
      multiBrand: search.multiBrand,
    },
    { isEnabled: options.isEnabled }
  )

  const resolveLocation = useMemo(() => {
    if (data?.length) {
      const foundLocation = data.find(
        (value) =>
          value.id === search.id &&
          (!search.type || value.type === search.type) &&
          (!search.countryCode || value.countryCode === search.countryCode)
      )
      if (foundLocation) {
        return {
          ...foundLocation,
          ...(search.vendorBrandId && !search.multiBrand
            ? { vendorBrandId: search.vendorBrandId }
            : {}),
        }
      }
    }
    return undefined
  }, [data, search.id, search.type, search.countryCode, search.vendorBrandId, search.multiBrand])

  return { data: resolveLocation, isLoading }
}

export const useQueryStateToFormState = (
  queryState: CarHireSearchFormParams & { tab?: string }
): {
  isLoading: boolean
  formValues: CarHireSearchForm
} => {
  const multiBrandEnabled = useFeatureFlag('car-hire-multi-brand')
  const { pickUpLocation, dates, dropOffLocation, travelers, membershipCode, vendorBrandId } =
    queryState
  const shouldSearchPickUp = pickUpLocation !== undefined && pickUpLocation.id !== ''
  const shouldSearchDropOff = dropOffLocation !== undefined && dropOffLocation.id !== ''

  const pickUpLocationByIdQueryState = useLocationsSearchResolver(
    {
      id: pickUpLocation?.id ?? '',
      type: pickUpLocation?.type ?? '',
      countryCode: pickUpLocation?.countryCode,
      vendorBrandId,
      multiBrand: multiBrandEnabled,
    },
    { isEnabled: shouldSearchPickUp }
  )
  const resolvedPickUpLocation = useMemo<CarHireLocation>(
    () => pickUpLocationByIdQueryState.data || DEFAULT_LOCATION,
    [pickUpLocationByIdQueryState.data]
  )

  const dropOffLocationByIdQueryState = useLocationsSearchResolver(
    {
      id: dropOffLocation?.id ?? '',
      type: dropOffLocation?.type ?? '',
      countryCode: dropOffLocation?.countryCode,
      vendorBrandId,
      multiBrand: multiBrandEnabled,
    },
    { isEnabled: shouldSearchDropOff }
  )
  const resolvedDropOffLocation = useMemo<CarHireLocation>(
    () => dropOffLocationByIdQueryState.data || DEFAULT_LOCATION,
    [dropOffLocationByIdQueryState.data]
  )

  const travellerFormValues = useMemo(
    () => ({
      isUnderAgeLimit: travelers?.[0]?.dateOfBirth
        ? differenceInYears(new Date(), new Date(travelers[0]?.dateOfBirth)) <
          DRIVER_AGE_LIMIT_IN_YEARS
        : false,
      dob: travelers?.[0]?.dateOfBirth
        ? new Date(travelers[0].dateOfBirth)
        : add(sub(new Date(), { years: DRIVER_AGE_LIMIT_IN_YEARS }), {
            days: DRIVER_AGE_LIMIT_BEFORE_DAYS,
          }),
      membershipCode: membershipCode ?? undefined,
      isAvisPreferred: membershipCode !== undefined,
    }),
    [membershipCode, travelers]
  )

  const pickUpFormValues = useMemo(
    () => ({
      pickUpSearchString: queryState.pickUpSearchString ?? (resolvedPickUpLocation?.fullName || ''),
      pickUpDate: dates?.startDateTime
        ? new Date(dates.startDateTime)
        : add(new Date(), { days: 1 }),
      pickUpTime: getTimes(dates?.startDateTime),
      pickUpLocation: resolvedPickUpLocation,
    }),
    [dates, queryState.pickUpSearchString, resolvedPickUpLocation]
  )

  const dropOffFormValues = useMemo(
    () => ({
      dropOffSearchString:
        queryState.dropOffSearchString ?? (resolvedDropOffLocation?.fullName || ''),
      dropOffDate: dates?.endDateTime ? new Date(dates.endDateTime) : add(new Date(), { days: 3 }),
      dropOffTime: getTimes(dates?.endDateTime),
      dropOffLocation: resolvedDropOffLocation,
    }),
    [dates, queryState.dropOffSearchString, resolvedDropOffLocation]
  )

  return {
    isLoading: pickUpLocationByIdQueryState.isLoading || dropOffLocationByIdQueryState.isLoading,
    formValues: {
      ...pickUpFormValues,
      ...dropOffFormValues,
      ...travellerFormValues,
      differentDropOff: Boolean(
        dropOffLocation && !isSameCarHirePlace(pickUpLocation, dropOffLocation)
      ),
    },
  }
}
